import { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import { useLocation } from "react-router-dom";
import { X, ChevronLeft, ChevronRight, HelpCircle, SkipForward } from "lucide-react";
import WALKTHROUGH_STEPS from "./walkthroughSteps";

const WalkthroughContext = createContext();

export function useWalkthrough() {
  return useContext(WalkthroughContext);
}

const STORAGE_KEY = "avida_walkthrough_seen";

function getSeenPages() {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}");
  } catch { return {}; }
}

function markPageSeen(path) {
  const seen = getSeenPages();
  seen[path] = Date.now();
  localStorage.setItem(STORAGE_KEY, JSON.stringify(seen));
}

function Tooltip({ step, currentIndex, totalSteps, onNext, onBack, onSkip, onDone }) {
  const ref = useRef(null);
  const [pos, setPos] = useState({ top: 0, left: 0, arrowDir: "top" });

  useEffect(() => {
    const el = document.querySelector(step.target);
    if (!el) return;

    const compute = () => {
      const rect = el.getBoundingClientRect();
      const ttW = 320;
      const ttH = ref.current?.offsetHeight || 160;
      const gap = 12;
      let top, left, arrowDir;

      if (step.position === "bottom" || (!step.position && rect.bottom + ttH + gap < window.innerHeight)) {
        top = rect.bottom + gap;
        left = rect.left + rect.width / 2 - ttW / 2;
        arrowDir = "top";
      } else if (step.position === "top") {
        top = rect.top - ttH - gap;
        left = rect.left + rect.width / 2 - ttW / 2;
        arrowDir = "bottom";
      } else if (step.position === "left") {
        top = rect.top + rect.height / 2 - ttH / 2;
        left = rect.left - ttW - gap;
        arrowDir = "right";
      } else {
        top = rect.top + rect.height / 2 - ttH / 2;
        left = rect.right + gap;
        arrowDir = "left";
      }

      left = Math.max(12, Math.min(left, window.innerWidth - ttW - 12));
      top = Math.max(12, Math.min(top, window.innerHeight - ttH - 12));

      setPos({ top, left, arrowDir });

      el.scrollIntoView({ behavior: "smooth", block: "nearest" });
      el.style.position = "relative";
      el.style.zIndex = "9998";
      el.style.boxShadow = "0 0 0 4px rgba(24,119,242,0.25), 0 0 0 2px rgba(24,119,242,0.5)";
      el.style.borderRadius = "8px";
      el.style.transition = "box-shadow 0.3s ease";
    };

    compute();
    window.addEventListener("resize", compute);
    return () => {
      window.removeEventListener("resize", compute);
      el.style.zIndex = "";
      el.style.boxShadow = "";
      el.style.borderRadius = "";
      el.style.transition = "";
    };
  }, [step]);

  const isLast = currentIndex === totalSteps - 1;
  const isFirst = currentIndex === 0;

  const arrowStyles = {
    top: { top: "-6px", left: "50%", transform: "translateX(-50%) rotate(45deg)" },
    bottom: { bottom: "-6px", left: "50%", transform: "translateX(-50%) rotate(45deg)" },
    left: { left: "-6px", top: "50%", transform: "translateY(-50%) rotate(45deg)" },
    right: { right: "-6px", top: "50%", transform: "translateY(-50%) rotate(45deg)" },
  };

  return (
    <>
      {/* Visual scrim — pointer-events disabled so it does NOT block clicks
          on the underlying page. Users dismiss via the × / Skip buttons in
          the tooltip, or by pressing Escape. Previously the scrim had
          pointerEvents:auto + onClick:onSkip, which swallowed the user's
          first click on ANY non-spotlit element. */}
      <div
        style={{
          position: "fixed",
          inset: 0,
          zIndex: 9996,
          background: "rgba(0,0,0,0.18)",
          pointerEvents: "none",
        }}
        aria-hidden="true"
      />
      <div
        ref={ref}
        style={{
          position: "fixed",
          top: pos.top,
          left: pos.left,
          zIndex: 9999,
          width: 320,
          pointerEvents: "auto",
        }}
        className="animate-in fade-in slide-in-from-bottom-2 duration-200"
      >
        <div className="bg-white rounded-xl shadow-2xl border border-slate-200 overflow-hidden">
          <div
            style={{
              position: "absolute",
              width: 12,
              height: 12,
              background: "white",
              border: "1px solid #e2e8f0",
              borderRight: "none",
              borderBottom: "none",
              ...arrowStyles[pos.arrowDir],
            }}
          />
          <div className="px-4 pt-3 pb-2 border-b border-slate-100 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-5 h-5 rounded-full bg-blue-500 flex items-center justify-center">
                <span className="text-[9px] font-bold text-white">{currentIndex + 1}</span>
              </div>
              <span className="text-xs font-bold text-slate-800">{step.title}</span>
            </div>
            <button onClick={onSkip} className="text-slate-400 hover:text-slate-600 transition-colors">
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
          <div className="px-4 py-3">
            <p className="text-xs text-slate-600 leading-relaxed">{step.text}</p>
          </div>
          <div className="px-4 pb-3 flex items-center justify-between">
            <div className="flex items-center gap-1">
              {Array.from({ length: totalSteps }).map((_, i) => (
                <div
                  key={i}
                  className={`w-1.5 h-1.5 rounded-full transition-colors ${i === currentIndex ? "bg-blue-500" : i < currentIndex ? "bg-blue-200" : "bg-slate-200"}`}
                />
              ))}
            </div>
            <div className="flex items-center gap-1.5">
              {!isLast && (
                <button
                  onClick={onSkip}
                  className="flex items-center gap-1 px-2 py-1 text-[10px] font-medium text-slate-400 hover:text-slate-600 transition-colors"
                >
                  <SkipForward className="w-3 h-3" /> Skip
                </button>
              )}
              {!isFirst && (
                <button
                  onClick={onBack}
                  className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[10px] font-semibold text-slate-600 bg-slate-100 hover:bg-slate-200 transition-colors"
                >
                  <ChevronLeft className="w-3 h-3" /> Back
                </button>
              )}
              <button
                onClick={isLast ? onDone : onNext}
                className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-[10px] font-semibold text-white bg-blue-500 hover:bg-blue-600 transition-colors"
              >
                {isLast ? "Done" : <>Next <ChevronRight className="w-3 h-3" /></>}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export function WalkthroughProvider({ children }) {
  const location = useLocation();
  const [active, setActive] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [currentPath, setCurrentPath] = useState(null);
  const timerRef = useRef(null);

  const getStepsForPath = useCallback((path) => {
    // Exact match first
    if (WALKTHROUGH_STEPS[path]) return WALKTHROUGH_STEPS[path];
    // Try without trailing slash
    const clean = path.replace(/\/$/, "");
    if (WALKTHROUGH_STEPS[clean]) return WALKTHROUGH_STEPS[clean];
    return null;
  }, []);

  const startWalkthrough = useCallback((path) => {
    const steps = getStepsForPath(path);
    if (!steps || steps.length === 0) return;
    setCurrentPath(path);
    setCurrentStep(0);
    setActive(true);
  }, [getStepsForPath]);

  const stopWalkthrough = useCallback(() => {
    setActive(false);
    setCurrentStep(0);
    if (currentPath) markPageSeen(currentPath);
    setCurrentPath(null);
  }, [currentPath]);

  // Escape key dismisses the walkthrough — replaces the old "click scrim to
  // close" shortcut now that the scrim is pointer-events:none.
  useEffect(() => {
    if (!active) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        stopWalkthrough();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [active, stopWalkthrough]);

  // Auto-trigger on first visit
  useEffect(() => {
    if (timerRef.current) clearTimeout(timerRef.current);

    timerRef.current = setTimeout(() => {
      const path = location.pathname;
      const seen = getSeenPages();
      const steps = getStepsForPath(path);

      // Suppress auto-launch coach-marks for QA / RBAC test accounts so that
      // automated harnesses aren't blocked by the tooltip overlay on every
      // page. Real users (e.g. @avidahealth.org / @gmail.com) are unaffected.
      let qaUser = false;
      try {
        const u = JSON.parse(localStorage.getItem("user") || "null");
        const email = (u?.email || "").toLowerCase();
        qaUser = !!email && (email.endsWith("@example.com") || email.includes(".test@"));
      } catch { qaUser = false; }

      if (steps && steps.length > 0 && !seen[path] && !active && !qaUser) {
        // Check if first target element exists
        const firstEl = document.querySelector(steps[0].target);
        if (firstEl) {
          // Mark seen as soon as it auto-launches so it shows at MOST once per
          // user — navigating away without pressing Done/Skip no longer
          // re-triggers the tour on the next visit.
          markPageSeen(path);
          startWalkthrough(path);
        }
      }
    }, 1500); // Delay to let page render

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [location.pathname, getStepsForPath, active, startWalkthrough]);

  const steps = currentPath ? (getStepsForPath(currentPath) || []) : [];
  const step = steps[currentStep];

  const handleNext = () => {
    if (currentStep < steps.length - 1) setCurrentStep(currentStep + 1);
  };

  const handleBack = () => {
    if (currentStep > 0) setCurrentStep(currentStep - 1);
  };

  const contextValue = {
    startWalkthrough,
    stopWalkthrough,
    active,
    hasSteps: (path) => {
      const s = getStepsForPath(path || location.pathname);
      return s && s.length > 0;
    },
  };

  return (
    <WalkthroughContext.Provider value={contextValue}>
      {children}
      {active && step && (
        <Tooltip
          step={step}
          currentIndex={currentStep}
          totalSteps={steps.length}
          onNext={handleNext}
          onBack={handleBack}
          onSkip={stopWalkthrough}
          onDone={stopWalkthrough}
        />
      )}
    </WalkthroughContext.Provider>
  );
}

export function WalkthroughHelpButton() {
  const { startWalkthrough, active, hasSteps } = useWalkthrough();
  const location = useLocation();

  if (active || !hasSteps(location.pathname)) return null;

  return (
    <button
      onClick={() => startWalkthrough(location.pathname)}
      className="fixed bottom-6 right-6 z-[9990] w-10 h-10 rounded-full bg-blue-500 hover:bg-blue-600 text-white shadow-lg hover:shadow-xl flex items-center justify-center transition-all hover:scale-110"
      title="Page walkthrough"
      data-testid="walkthrough-help-btn"
    >
      <HelpCircle className="w-5 h-5" />
    </button>
  );
}
